var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};

// elements/components/x-sidebar.ts
import "@adornis/router/client/make-router.js";
import { routingStateSignal } from "@adornis/router/client/routing-state-signal.js";
import { TranslationController } from "@adornis/translation-core/client/translation-controller.js";
import { SignalWatcher } from "@lit-labs/preact-signals";
import { html, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { BehaviorSubject, take, takeUntil } from "rxjs";
import { ChemistryLitElement } from "../../chemistry-lit-element.js";
import { RXController } from "../../controllers/RXController.js";
import { WindowSizeController } from "../../controllers/window-size-controller.js";
import { css } from "../../directives/css.js";
import { xComponents } from "../../elements/x-components.js";
import "./x-button.js";
import "./x-flex.js";
import "./x-hr.js";
import "./x-icon.js";
import "./x-link.js";
import "./x-vr.js";
var isSidebarOpenSubject = new BehaviorSubject(false);
var XSidebar = class extends ChemistryLitElement {
  constructor() {
    super(...arguments);
    this.isOpen = new RXController(this, isSidebarOpenSubject);
    this.logoRouteTo = "/";
    this.size = new WindowSizeController(this);
    this.routes = [];
    this.translation = new TranslationController(this);
    this.isNarrow = false;
  }
  styles() {
    return [
      {
        ":host": {
          height: "100%",
          width: this.isOpen.value ? this.size.mobile ? "100%" : this.isNarrow ? "56px" : "256px" : "0",
          position: this.size.mobile ? "fixed" : "static",
          background: this.colors.light,
          display: "flex",
          transform: !this.isOpen.value ? "translateX(-100vw)" : "translateX(0)",
          transition: "transform .2s ease",
          zIndex: "1000",
          boxSizing: "border-box",
          paddingTop: `calc(${this.spacing.md} + env(safe-area-inset-top))`,
          paddingBottom: "env(safe-area-inset-bottom)"
        }
      }
    ];
  }
  static isOpen() {
    return isSidebarOpenSubject.value;
  }
  static open() {
    isSidebarOpenSubject.next(true);
  }
  static close() {
    isSidebarOpenSubject.next(false);
  }
  static toggle() {
    isSidebarOpenSubject.next(!isSidebarOpenSubject.getValue());
  }
  connectedCallback() {
    super.connectedCallback();
    this.size.observable.pipe(take(1), takeUntil(this.disconnected)).subscribe((size) => {
      if (size.mobile)
        isSidebarOpenSubject.next(false);
      else
        isSidebarOpenSubject.next(true);
    });
  }
  render() {
    return xComponents(html`
      <x-flex space="md" w100>
        ${this.size.mobile ? html`
              <x-button
                mode="icon"
                icon="close"
                ${css({ alignSelf: "end", marginRight: this.spacing.md })}
                @click=${() => isSidebarOpenSubject.next(!isSidebarOpenSubject.getValue())}
              ></x-button>
            ` : nothing}
        ${!this.size.mobile ? this._logo() : nothing}
        <x-flex ${css({ overflowY: "auto" })} space-between h100>
          ${this.size.mobile ? this._logo() : nothing}

          <x-flex flex>
            <slot></slot>
            ${this.routes.map(
      (r) => r.metaData?.visible ?? true ? xComponents(html`
                    <x-sidebar-routing-item ?is-narrow=${this.isNarrow} .routeConfig=${r}> </x-sidebar-routing-item>
                  `) : nothing
    )}
          </x-flex>

          <x-flex>
            <x-flex padding=${this.isNarrow ? "xs" : "sm"} ?horizontal=${!this.isNarrow} space-between>
              <slot name="bottom"></slot>
            </x-flex>
          </x-flex>
        </x-flex>
      </x-flex>
    `);
  }
  _logo() {
    return xComponents(html`
      <x-flex crossaxis-center>
        <x-link href=${this.logoRouteTo}>
          <slot name="logo"></slot>
        </x-link>
      </x-flex>
    `);
  }
};
__decorateClass([
  state()
], XSidebar.prototype, "isOpen", 2);
__decorateClass([
  property({ attribute: true, type: String })
], XSidebar.prototype, "logoRouteTo", 2);
__decorateClass([
  state()
], XSidebar.prototype, "size", 2);
__decorateClass([
  state()
], XSidebar.prototype, "routes", 2);
__decorateClass([
  state()
], XSidebar.prototype, "translation", 2);
__decorateClass([
  property({ type: Boolean, attribute: "is-narrow" })
], XSidebar.prototype, "isNarrow", 2);
XSidebar = __decorateClass([
  customElement("x-sidebar")
], XSidebar);
var XSidebarItem = class extends SignalWatcher(ChemistryLitElement) {
  constructor() {
    super(...arguments);
    this.active = false;
    this.isNarrow = false;
    this.validateActive = (currentUrl, href) => {
      if (!href)
        return false;
      if (href === "/")
        return currentUrl === "/";
      return currentUrl.startsWith(href);
    };
    this.size = new WindowSizeController(this);
  }
  styles() {
    if (this.isNarrow)
      return [
        {
          ":host": {
            borderRadius: this.sizes.borderRadius,
            background: this.active ? this.colors.accent : "transparent",
            color: this.active ? this.colors.on.accent : "unset",
            transition: "all .12s ease",
            "&:hover": { background: this.active ? this.colors.accent : this.colors.accent.shade(100) },
            marginLeft: this.spacing.xs,
            marginRight: this.spacing.xs
          }
        }
      ];
    return [
      {
        ":host": {
          background: this.active ? this.colors.accent : "transparent",
          color: this.active ? this.colors.on.accent : "unset",
          transition: "all .12s ease",
          borderTopLeftRadius: this.sizes.borderRadius,
          borderBottomLeftRadius: this.sizes.borderRadius,
          marginLeft: this.spacing.xs,
          "&:hover": { background: this.active ? this.colors.accent : this.colors.accent.shade(100) }
        },
        "x-icon": { fontSize: "130%" }
      }
    ];
  }
  render() {
    return xComponents(html`
      <x-link
        href=${this.href || "#"}
        ${css({
      width: "100%",
      padding: this.isNarrow ? this.spacing.sm : `${this.spacing.sm} ${this.spacing.md}`,
      boxSizing: "border-box"
    })}
        @click=${() => {
      if (this.size.mobile)
        isSidebarOpenSubject.next(false);
    }}
      >
        <x-flex horizontal crossaxis-center space="md" w100 title=${this.text}>
          ${this.iconDisplay()} ${this.text && !this.isNarrow ? html`<x-text> ${this.text} </x-text>` : nothing}
          <slot></slot>
        </x-flex>
      </x-link>
    `);
  }
  iconDisplay() {
    if (this.icon && this.icon.charAt(0) === "/") {
      return html` <x-icon src=${this.icon} type="img"> </x-icon> `;
    }
    return html` <x-icon> ${this.icon || "keyboard_arrow_right"} </x-icon> `;
  }
  willUpdate(_changedProperties) {
    super.willUpdate(_changedProperties);
    if (this.validateActive) {
      this.active = this.validateActive(routingStateSignal.value.path, this.href);
    } else {
      this.active = this.href && routingStateSignal.value.path.startsWith(this.href) || false;
    }
  }
};
__decorateClass([
  property()
], XSidebarItem.prototype, "href", 2);
__decorateClass([
  property()
], XSidebarItem.prototype, "icon", 2);
__decorateClass([
  property()
], XSidebarItem.prototype, "text", 2);
__decorateClass([
  property({ reflect: true, type: Boolean })
], XSidebarItem.prototype, "active", 2);
__decorateClass([
  property({ type: Boolean, attribute: "is-narrow" })
], XSidebarItem.prototype, "isNarrow", 2);
XSidebarItem = __decorateClass([
  customElement("x-sidebar-item")
], XSidebarItem);
var XSidebarRoutingItem = class extends XSidebarItem {
  constructor() {
    super(...arguments);
    this.routeConfig = null;
    this.translation = new TranslationController(this);
  }
  willUpdate(_changedProperties) {
    super.willUpdate(_changedProperties);
    if (_changedProperties.has("routeConfig") && this.routeConfig) {
      this.href = this.routeConfig.metaData?.href ?? this.routeConfig.path.replace("*", "").replaceAll(/\:[^\:\/]+/g, "");
      this.icon = this.routeConfig.metaData?.icon;
      this.text = this.translation.translate("route", this.routeConfig.name);
    }
  }
};
__decorateClass([
  state()
], XSidebarRoutingItem.prototype, "routeConfig", 2);
__decorateClass([
  state()
], XSidebarRoutingItem.prototype, "translation", 2);
XSidebarRoutingItem = __decorateClass([
  customElement("x-sidebar-routing-item")
], XSidebarRoutingItem);
export {
  XSidebar,
  XSidebarItem,
  XSidebarRoutingItem,
  isSidebarOpenSubject
};
